import { Lock, AlertTriangle } from "lucide-react";
import { TokenReminder } from "@/components/TokenReminder";
import { RecorderReadyBanner } from "@/components/RecorderReadyBanner";
import { RecorderHealthBanner } from "@/components/RecorderHealthBanner";
import { toast } from "sonner";
import { api, STRATEGY_LABELS } from "@/lib/api";
import { ThemeToggle } from "@/lib/theme";

const STATUS = {
  connected: { label: "Connected", cls: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" },
  expired: { label: "Token Expired", cls: "text-amber-400 border-amber-500/30 bg-amber-500/10" },
  error: { label: "Error", cls: "text-red-400 border-red-500/30 bg-red-500/10" },
  not_configured: { label: "Not Connected", cls: "text-slate-400 border-slate-600 bg-slate-800/50" },
};

const fmtLeft = (h) => {
  if (h === null || h === undefined) return "unknown";
  if (h <= 0) return "expired";
  return `${Math.floor(h)}h ${Math.round((h % 1) * 60)}m`;
};

export const TopBar = ({ status, onGoSettings, onGoLive, onGoBacktest }) => {
  const onLiveSwitch = async () => {
    onGoLive();
    let st;
    try { st = (await api.get("/execution/status")).data; } catch { return; }
    const failed = st.gates.filter((g) => !g.ok);
    const focus = (sel) => setTimeout(() => document.querySelector(sel)?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
    if (st.live_state === "armed") {
      toast("Live trading is ARMED. Use Disarm or the Kill Switch on this tab to stop it.");
      focus("[data-testid=panel-arm]");
    } else if (st.live_state === "killed") {
      toast.error("The kill switch is engaged. Type the confirmation phrase and click Reset kill switch first.");
      focus("[data-testid=panel-arm]");
    } else if (failed.length) {
      toast.error(`LIVE is locked: ${failed.length} gate${failed.length > 1 ? "s" : ""} failing`, {
        description: (
          <ul data-testid="toast-failed-gates" className="mt-1 space-y-0.5">
            {failed.map((g) => (
              <li key={g.key}>✕ {g.label}{g.detail !== null && g.detail !== undefined && typeof g.detail !== "object" ? ` (${g.detail})` : ""}</li>
            ))}
          </ul>
        ),
        duration: 10000,
      });
      focus("[data-testid=panel-live-gates]");
    } else {
      toast.success("All gates pass. Type the confirmation phrase and click Arm live trading.");
      focus("[data-testid=panel-arm]");
      setTimeout(() => document.querySelector("[data-testid=input-arm-phrase]")?.focus(), 400);
    }
  };
  const st = STATUS[status?.status || "not_configured"];
  const needsToken = status && status.status !== "connected";
  return (
    <header data-testid="header-container" className="border-b border-slate-800 bg-[#080C14]">
      <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-3 sm:px-4">
        <div className="flex items-center gap-3">
          <div className="h-7 w-1 rounded-sm bg-indigo-500" />
          <div>
            <h1 className="font-display text-lg font-bold tracking-tight sm:text-xl">NIFTY ORB DESK</h1>
            <p className="eyebrow !text-[10px]">{status?.strategy_type && status.strategy_type !== "orb" ? STRATEGY_LABELS[status.strategy_type] : "ORB · VWAP · EMA 20/50 · RSI"} — research, paper &amp; gated live trading</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <ThemeToggle />
          {status?.live_state === "armed" ? (
            <span data-testid="badge-paper-mode" className="pulse-dot rounded border border-red-500/60 bg-red-500/15 px-2 py-1 font-semibold tracking-wider text-red-400">LIVE ARMED</span>
          ) : (
            <span data-testid="badge-paper-mode" className="rounded border border-orange-500/40 bg-orange-500/10 px-2 py-1 font-semibold tracking-wider text-orange-400">PAPER MODE</span>
          )}
          <span data-testid="badge-dhan-status" className={`flex items-center gap-1.5 rounded border px-2 py-1 font-medium ${st.cls}`}>
            <span className={`h-1.5 w-1.5 rounded-full bg-current ${status?.status === "connected" ? "pulse-dot" : ""}`} />
            Dhan: {st.label}
          </span>
          {status && (
            <span data-testid="badge-data-access" className={`rounded border px-2 py-1 font-medium ${status.data_source === "free" ? "border-amber-500/30 bg-amber-500/10 text-amber-400" : status.data_access === false ? "border-red-500/30 bg-red-500/10 text-red-400" : "border-slate-700 text-slate-400"}`}>
              Data: {status.data_source === "free" ? "Free (Yahoo · NSE)" : status.data_access === false ? "Dhan · no subscription" : "Dhan"}
            </span>
          )}
          <span data-testid="token-expiry-label" className="num rounded border border-slate-700 px-2 py-1 text-slate-400">
            Token expires in {fmtLeft(status?.hours_left)}
          </span>
          <button
            data-testid="toggle-locked-live-switch"
            onClick={onLiveSwitch}
            title={status?.live_state === "armed" ? "Live trading armed: click to manage" : "Click to see why LIVE is locked, or to arm it when every gate passes"}
            className={`flex items-center gap-2 rounded border px-2 py-1 transition-colors ${status?.live_state === "armed" ? "border-red-500/50 text-red-400" : "border-slate-700 bg-slate-900 text-slate-500 hover:text-slate-300"}`}
          >
            <Lock size={12} /> LIVE
            <span className={`relative h-4 w-7 rounded-full ${status?.live_state === "armed" ? "bg-red-600" : "bg-slate-700"}`}>
              <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-slate-300 transition-[left] ${status?.live_state === "armed" ? "left-3.5" : "left-0.5"}`} />
            </span>
            {({ locked: "LOCKED", ready: "DISARMED", armed: "ARMED", killed: "KILLED" })[status?.live_state || "locked"]}
          </button>
        </div>
      </div>
      {status?.proxy_health && !status.proxy_health.ok && (
        <div data-testid="banner-proxy-down" className="flex flex-wrap items-center gap-2 border-t border-red-500/30 bg-red-500/15 px-3 py-2 text-xs text-red-300 sm:px-4">
          <AlertTriangle size={14} />
          <b>VPS proxy not responding</b>
          <span>({status.proxy_health.failures} failed checks{status.proxy_health.last_ok ? `, last OK ${new Date(status.proxy_health.last_ok).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour12: false })} IST` : ""}). Dhan orders and data cannot reach your whitelisted IP; live arming is paused.</span>
          <button data-testid="banner-proxy-go-live" onClick={onGoLive} className="ml-auto underline underline-offset-2">Check</button>
        </div>
      )}
      <RecorderReadyBanner ready={status?.recorder_ready} onGoBacktest={onGoBacktest} />
      <RecorderHealthBanner health={status?.recorder_health} onGoBacktest={onGoBacktest} />
      {["connected", "expired"].includes(status?.status) && <TokenReminder alert={status.token_alert} onRenew={onGoSettings} />}
      {needsToken && (
        <div data-testid="banner-token-expiry" className="flex flex-wrap items-center gap-2 border-t border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-300 sm:px-4">
          <AlertTriangle size={14} />
          {status.status === "expired"
            ? "Your Dhan Access Token has expired. Paste today's token to resume live data."
            : status.status === "error"
            ? `Dhan connection error: ${status.error || "unknown"}`
            : status.data_source === "free"
            ? "Dhan is not connected. Using free, possibly delayed data; account funds and positions are unavailable."
            : "Dhan is not connected. Add your Client ID and Access Token to load market data."}
          <button data-testid="banner-go-settings" onClick={onGoSettings} className="ml-auto underline underline-offset-2 hover:text-amber-200">
            Open Settings
          </button>
        </div>
      )}
    </header>
  );
};
